/**
 * SelectRole
 * Interstitial shown straight after login when an employee holds more than one role.
 *
 * Why it exists: a role's modules come from its menu profile (app_prfle_lst_t, category
 * MNP), and the ERP loads exactly ONE menu profile per session. An employee assigned as
 * both, say, Junior Assistant (role "UDA Clerk") and Administrative Officer (role "UDA
 * Admin") therefore has two completely different sidebars available and must say which
 * one to open. Picking here posts to /select-assignment, which re-issues the JWT with the
 * chosen role's menu/setup/report profiles and lands on that role's start page.
 *
 * Employees with a single role never see this screen — login goes straight through.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, redirectToUserMainPage } from '../../../../context/AuthContext';
import AuthShell from '../components/AuthShell';

const SelectRole: React.FC = () => {
  const { roles, selectRole, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [submittingRoleId, setSubmittingRoleId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Guard the route directly: reaching it without a session, or with nothing to choose
  // between, should never strand the user on an empty screen.
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (roles.length === 1) {
      // Only one role after all — apply it silently rather than asking a pointless question.
      redirectToUserMainPage();
    } else if (roles.length === 0) {
      redirectToUserMainPage();
    }
  }, [isAuthenticated, roles, navigate]);

  const handleSelect = async (assignmentId: number, roleId: number) => {
    if (submittingRoleId !== null) return; // ignore double-clicks; the page navigates away on success
    setError(null);
    setSubmittingRoleId(roleId);
    try {
      await selectRole(assignmentId, roleId);
      // selectRole performs a full document load into the role's landing page.
    } catch (e: any) {
      setError(e?.message || 'Could not open that role. Please try again.');
      setSubmittingRoleId(null);
    }
  };

  if (!isAuthenticated || roles.length < 2) {
    return null;
  }

  return (
    <AuthShell bandLabel="DTCP ERP · Select role">
      <div className="text-center mb-6">
        <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-gray-400">
          {user?.name ? `Signed in as ${user.name}` : 'Signed in'}
        </p>
        <h2 className="mt-1 text-xl sm:text-2xl font-bold tracking-tight animated-gradient-text">
          Select your role
        </h2>
        <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-amber-500 to-amber-600"></div>
        <p className="mt-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
          You hold more than one role. Choose the one you want to work in — each opens its
          own set of modules.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <ul className="space-y-3">
        {roles.map((role) => {
          const isSubmitting = submittingRoleId === role.rle_id;
          const isDisabled = submittingRoleId !== null && !isSubmitting;
          return (
            <li key={`${role.assignment_id}-${role.rle_id}`}>
              <button
                type="button"
                onClick={() => handleSelect(role.assignment_id, role.rle_id)}
                disabled={submittingRoleId !== null}
                aria-busy={isSubmitting}
                className={`w-full text-left rounded-xl border p-4 transition
                  border-slate-200 dark:border-gray-700
                  bg-white dark:bg-gray-900/40
                  hover:border-amber-500 hover:shadow-md
                  focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1
                  ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                  ${isSubmitting ? 'border-amber-500 shadow-md' : ''}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 dark:text-gray-100 truncate">
                        {role.rle_nm}
                      </span>
                      {role.is_primary && (
                        <span className="shrink-0 rounded bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                          Primary
                        </span>
                      )}
                    </div>
                    {/* The position/designation is what the employee recognises day to day;
                        the role name above is the RBAC label that drives the menus. */}
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 truncate">
                      {[role.designation_name || role.position_name, role.department_name]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <span className="shrink-0 text-amber-600 dark:text-amber-400">
                    {isSubmitting ? (
                      <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
                    ) : (
                      <i className="ri-arrow-right-line text-lg" aria-hidden="true"></i>
                    )}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <p className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500">
        You can switch roles later from the header without signing out.
      </p>
    </AuthShell>
  );
};

export default SelectRole;
